import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SectionHeader, StarRating } from "@/components/shared/Common";
import { Star, TrendingUp, MessageSquare, Mail, Send, ChevronRight } from "lucide-react";

const CATS = [
  { key: "overall_experience",         label: "Overall experience" },
  { key: "payment_on_time",            label: "Payment on time" },
  { key: "professional_communication", label: "Professional communication" },
  { key: "respect_to_staff",           label: "Respect to staff" },
  { key: "job_description_accuracy",   label: "Job description accuracy" },
  { key: "sops_and_briefing",          label: "SOPs & briefing" },
  { key: "deployment_organisation",    label: "Deployment organisation" },
  { key: "fair_treatment",             label: "Fair treatment" },
  { key: "site_safety",                label: "Site safety" },
];

function Bar({ label, value }) {
  const v = Number(value) || 0;
  const color = v >= 4 ? "#D4A24C" : v >= 3 ? "#0066FF" : v >= 2 ? "#F59E0B" : "#EF4444";
  return (
    <div className="grid grid-cols-[220px_1fr_60px] gap-3 items-center py-1.5" data-testid={`co-cat-${label}`}>
      <div className="text-sm text-white font-manrope">{label}</div>
      <div className="h-2 bg-[#141414] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${(v / 5) * 100}%`, background: color }} />
      </div>
      <div className="text-right font-outfit font-bold text-white">{v.toFixed(2)}<span className="text-xs text-white/50">/5</span></div>
    </div>
  );
}

export default function CompanyReviews() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/company/my-review-summary");
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    try {
      const { data: bks } = await api.get("/bookings/mine");
      setPendingReviews((bks || []).filter((b) => b.status === "completed" && b.has_review === false));
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const toggleMonthly = async (enabled) => {
    try {
      await api.put("/company/monthly-digest-prefs", { enabled });
      toast.success(enabled ? "You'll receive a monthly review summary email." : "Monthly summary emails disabled.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      await api.post("/company/send-my-digest-now");
      toast.success("Summary emailed to you.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-white/60 font-manrope">Loading…</div>;

  const averages = data.review_averages || {};
  const overall = Number(averages.overall_experience || 0);

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="How your staff rate you"
        title="Confidential feedback dashboard"
        subtitle="This is what independent, verified security professionals are saying about deploying with your company. Reviewer identities are never shared. Use these signals to self-correct before any admin QA threshold is triggered."
      />

      {pendingReviews.length > 0 && (
        <div className="card-flat p-4 border-l-4 border-l-amber-400" data-testid="my-reviews-pending-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-outfit font-semibold text-white">
                Reviews you owe: {pendingReviews.length} completed booking{pendingReviews.length === 1 ? "" : "s"} awaiting your feedback
              </div>
              <p className="text-sm text-white/60 font-manrope">
                Rating pros after every job is what keeps our rankings honest — and helps the best staff surface first for you next time.
              </p>
              <div className="mt-3 grid gap-2">
                {pendingReviews.slice(0, 5).map((b) => (
                  <Link
                    key={b.id}
                    to={`/company/bookings/${b.id}#review`}
                    className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 hover:bg-amber-100 px-3 py-2 text-sm font-manrope"
                    data-testid={`my-reviews-pending-${b.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-outfit font-semibold text-white truncate">
                        {b.target_type === "professional" ? "Professional" : "Team"} booking · {b.date || "—"}
                      </div>
                      <div className="text-xs text-white/60">{b.workers}× · {b.hours}h</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-brand font-outfit font-bold text-xs uppercase tracking-widest">
                      Write review <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card-flat p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4 text-brand" /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Monthly summary email</div>
              <div className="font-outfit font-semibold text-white mt-1">Stay ahead of QA thresholds automatically.</div>
              <p className="text-sm text-white/60 font-manrope mt-1">We&apos;ll email you a rolling 30-day review summary with your top 3 improvement opportunities.</p>
              <label className="flex items-center gap-2 text-sm font-manrope mt-3">
                <Checkbox checked={!!data.monthly_digest_enabled} onCheckedChange={(v) => toggleMonthly(!!v)} data-testid="co-monthly-toggle" />
                Email me a monthly review summary
              </label>
              {data.last_monthly_digest_sent && (
                <div className="text-[11px] text-white/60 font-manrope mt-1">Last sent {new Date(data.last_monthly_digest_sent).toLocaleString()}</div>
              )}
            </div>
          </div>
          <Button onClick={sendNow} disabled={busy || data.review_count === 0} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="co-send-digest-now">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Email me now"}
          </Button>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Overall rating</div>
            <div className="flex items-center gap-4 mt-1">
              <div className="text-5xl font-outfit font-bold text-white">{overall.toFixed(2)}<span className="text-lg text-white/50">/5</span></div>
              <StarRating rating={Math.round(overall)} count={data.review_count} />
            </div>
            <div className="text-sm text-white/60 font-manrope mt-1 flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5" /> {data.review_count} reviews · {data.poor_review_count_12mo || 0} serious concerns in last 12 months
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Status</div>
            <div className={`inline-block px-3 py-1 rounded-full font-outfit font-bold text-xs uppercase tracking-widest ${
              data.qa_status === "clear" ? "bg-gold-50 text-gold-600 border border-gold-200" :
              "bg-amber-50 text-amber-700 border border-amber-200"
            }`}>
              {(data.qa_status || "clear").replace(/_/g, " ")}
            </div>
            {data.booking_blocked && (
              <div className="text-[11px] text-red-600 mt-1 font-manrope">New bookings currently blocked</div>
            )}
          </div>
        </div>

        <div className="mt-6 border-t pt-4">
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> Category averages
          </div>
          {data.review_count === 0
            ? <div className="text-sm text-white/60 font-manrope">No reviews yet — book a professional and complete the job to start receiving feedback.</div>
            : CATS.map((c) => <Bar key={c.key} label={c.label} value={averages[c.key]} />)}
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3">Anonymised written feedback</div>
        {(!data.recent_written || data.recent_written.length === 0) && (
          <div className="text-sm text-white/60 font-manrope">No written feedback yet.</div>
        )}
        <div className="space-y-3">
          {(data.recent_written || []).map((r, i) => (
            <div key={i} className="border border-white/10 rounded-md p-3 bg-[#0E0E0E]" data-testid={`co-written-${i}`}>
              <div className="text-xs text-white/60 font-manrope">
                <Star className="inline w-3 h-3 text-amber-400 fill-amber-400 mr-1" />
                Overall {r.overall_experience}/5 · {new Date(r.created_at).toLocaleDateString()}
              </div>
              <div className="text-sm text-white mt-1 whitespace-pre-wrap">&ldquo;{r.written}&rdquo;</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
